// Reconcile the tooth chart with the "Today's plan" reminders — single source
// of truth shared by the chairside (Doctor) and the checkout (ScheduledNeeds)
// screens so the two can never disagree.
//
// When the dentist charts a procedure that matches a booked reminder, the
// reminder auto-checks off. Chief-dentist guardrails:
//   • match by procedure_id;
//   • if the reminder names a tooth, the charted work must be on THAT tooth
//     (a composite on #26 must not tick off a #12 reminder);
//   • a generic (no-tooth) booking matches any same-procedure chart;
//   • one charted item consumes at most one reminder (one-to-one);
//   • reminders already handled (tapped Done, or Not-today/Declined/Logged)
//     are skipped so a chart flows to a still-pending one.
//
// Returns a Map: reminderKey -> { tooth, surface } (so the row can show
// "Done · from chart #12", never a silent checkmark).
//
// keyOf is passed in because the two screens compute the work key with their
// own (equivalent) helper.
export function reconcileChartToReminders(scheduledItems, doneItems, { keyOf, doneKeys, dispositions } = {}) {
  const map = new Map();
  if (typeof keyOf !== "function") return map;

  const reminders = (scheduledItems || []).filter((s) => {
    const k = keyOf(s);
    if (!k) return false;
    if (doneKeys?.has?.(k)) return false;
    const d = dispositions?.[k] || (s?.tn_id ? dispositions?.[`tn:${s.tn_id}`] : null);
    if (d) return false;
    return (s?.tooth_specific || s?.surface_specific) && !s?.treatment_plan_id && !s?.ortho_case_id;
  });
  if (reminders.length === 0) return map;

  const reminderKeys = new Set(reminders.map(keyOf));
  const charted = (doneItems || []).filter(
    (it) => (it.outcome || "done") === "done"
      && it.procedure_id
      && it.tooth_number
      && !reminderKeys.has(keyOf(it)),
  );

  const used = new Set();
  for (const rem of reminders) {
    const match = charted.find((it) => {
      if (used.has(it)) return false;
      if (String(it.procedure_id) !== String(rem.procedure_id)) return false;
      if (rem.tooth_number) return String(it.tooth_number) === String(rem.tooth_number);
      return true;
    });
    if (match) {
      used.add(match);
      map.set(keyOf(rem), { tooth: match.tooth_number, surface: match.surface || null });
    }
  }
  return map;
}
